import { h, JSX, RefCallback } from "preact"
import { alignValue, clampValue, isValueOutOfRange, noop } from "./utils"
import { AbstractSliderProps, AbstractSlider, SliderMarks } from "./AbstractSlider"
import { Handle } from "./Handle"
import { Track } from "./Track"
import { useCallback, useEffect, useRef, useState } from "preact/hooks"

/**
 * @group Components
 */
export interface MultiSliderProps extends AbstractSliderProps {
    /** Initial values of handles */
    defaultValue: number[]
    /** Current values of handles */
    value?: number[]
    /** How many ranges to render (handles count = count + 1) */
    count?: number
    /** Allow pushing of surrounding handles when moving with this distance */
    pushable?: boolean | number
    /** Allow handles to cross each other? */
    allowCross?: boolean
    /** Triggered before value is start to change (on mouse down, etc) */
    onBeforeChange?(value: number[]): void
    /** Triggered while the value of Slider changing */
    onChange(value: number[]): void
    /** Triggered after slider changes stop (on mouse up, etc) */
    onAfterChange(value: number[]): void
}

/**
 * Cache of slider points.
 */
interface PointsCache {
    marks: SliderMarks
    step: number
    points: number[]
}

/**
 * Slider with multiple handles.
 *
 * @group Components
 */
export function MultiSlider(props: MultiSliderProps): JSX.Element {
    const {
        count = 1,
        allowCross = true,
        pushable = false,
        value = null,
        defaultValue = null,
        min = 0,
        max = 100,
        classesPrefix = "",
        tipFormatter = String,
        step = 1,
        marks = {},
        dots = false,
        included = true,
        vertical = false,
        disabled = false,
        className = "range-slider",
        onBeforeChange = noop,
        onAfterChange = noop,
        onChange,
        children
    } = props

    /**
     * Cache of slider points.
     */
    const [pointsCache, setPointsCache] = useState<PointsCache | undefined>(undefined)
    const [bounds, setBounds] = useState<number[]>([])
    const [handle, setHandle] = useState<number | null>(null)
    const [recent, setRecent] = useState<number>(0)
    const setSliderRef = useCallback<RefCallback<HTMLDivElement>>(element => {
        sliderRef.current = element
    }, [])
    /**
     * Reference to slider in DOM.
     */
    const sliderRef = useRef<HTMLDivElement | null>(null)
    /**
     * References to handles in DOM.
     */
    const handlesRefs = useRef<Element[]>([])

    useEffect(() => {
        const initialValue = [...Array(count + 1)].map(() => min)

        const values = value != null ? value : defaultValue != null ? defaultValue : initialValue

        setBounds(values.map(value => clampAlignValue(value)))
        setRecent(bounds[0] === max ? 0 : bounds.length - 1)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const previousBounds = useRef(bounds)
    const previousProps = useRef(props)

    useEffect(() => {
        if (!(value || typeof min === "number" || typeof max === "number")) {
            return
        }

        const nextValue = value || previousBounds.current
        const nextBounds = nextValue.map(singleValue => clampAlignValue(singleValue, previousProps.current))

        if (
            nextBounds.length === previousBounds.current.length &&
            nextBounds.every((singleValue, index) => singleValue === previousBounds.current[index])
        ) {
            return
        }

        setBounds(nextBounds)

        if (previousBounds.current.some(singleValue => isValueOutOfRange(singleValue, { min, max }))) {
            onChange(nextBounds)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, min, max])

    const offsets = bounds.map(value => calcOffset(value))

    const handles = bounds.map((value, index) => (
        <Handle
            vertical={vertical}
            disabled={disabled}
            dragging={handle === index}
            min={min}
            max={max}
            value={value}
            index={index + 1}
            offset={offsets[index]!}
            classesPrefix={classesPrefix}
            refFn={(node: HTMLElement | null) => {
                saveHandle(node, index)
            }}
            key={`handle-${index}`}
            label={index ? "Upper bound" : "Lower bound"}
        >
            {tipFormatter(value)}
        </Handle>
    ))

    const tracks = bounds.slice(0, -1).map((_value, index) => {
        const nextIndex = index + 1

        return (
            <Track
                vertical={vertical}
                included={true}
                index={nextIndex}
                offset={offsets[index]!}
                length={offsets[nextIndex]! - offsets[index]!}
                classesPrefix={classesPrefix}
                key={`track-${index}`}
            />
        )
    })

    /**
     * Save reference to handle element.
     */
    function saveHandle(node: HTMLElement | null, index: number = 0) {
        if (!node) {
            delete handlesRefs.current[index]
        } else if (node instanceof HTMLElement) {
            handlesRefs.current[index] = node
        }
    }

    /**
     * Get position, where slider starts in document (pixels).
     */
    function getSliderStart(): number {
        const slider = sliderRef.current

        if (!slider) {
            return 0
        }

        const rect = slider.getBoundingClientRect()

        return vertical ? rect.top : rect.left
    }

    /**
     * Calc slider value based on a position in document.
     */
    function calcValueByPos(position: number): number {
        const pixelOffset = position - getSliderStart()
        const nextValue = clampAlignValue(calcValue(pixelOffset))

        return nextValue
    }

    /**
     * Calc slider value based on offset from slider element start.
     */
    function calcValue(offset: number): number {
        const ratio = Math.abs(Math.max(offset, 0) / getSliderLength())
        const value = vertical ? (1 - ratio) * (max - min) + min : ratio * (max - min) + min

        return value
    }

    function getSliderLength(): number {
        const slider = sliderRef.current

        if (!slider) {
            return 0
        }

        return vertical ? slider.clientHeight : slider.clientWidth
    }

    /**
     * Calc offset in slider element based on a value.
     */
    function calcOffset(value: number): number {
        const ratio = (value - min) / (max - min)

        return ratio * 100
    }

    /**
     * When value changed.
     */
    function handleChange(newState: { handle?: number | null; bounds: number[] }): void {
        const isNotControlled = !value

        if (isNotControlled) {
            setBounds(newState.bounds)
            if (newState.handle) {
                setHandle(newState.handle)
            }
        } else if (typeof newState.handle !== "undefined") {
            setHandle(newState.handle)
        }
        onChange(newState.bounds)
    }

    /**
     * On mouse/touch start.
     */
    function onStart(position: number): void {
        onBeforeChange?.(bounds)

        const value = calcValueByPos(position)

        const closestBound = getClosestBound(value)
        const boundNeedMoving = getBoundNeedMoving(value, closestBound)

        setHandle(boundNeedMoving)
        setRecent(boundNeedMoving)

        const prevValue = bounds[boundNeedMoving]

        if (value === prevValue) {
            return
        }

        const nextBounds = [...bounds]
        nextBounds[boundNeedMoving] = value
        handleChange({ bounds: nextBounds })
    }

    /**
     * On mouse/touch move.
     */
    function onMove(position: number): void {
        if (handle == null) {
            return
        }

        const value = calcValueByPos(position)
        const oldValue = bounds[handle]

        if (value === oldValue) {
            return
        }

        const nextBounds = [...bounds]
        nextBounds[handle] = value

        let nextHandle = handle

        if (pushable !== false) {
            const originalValue = bounds[nextHandle]
            pushSurroundingHandles(nextBounds, nextHandle, originalValue!)
        } else if (allowCross) {
            nextBounds.sort((a, b) => a - b)
            nextHandle = nextBounds.indexOf(value)
        }

        handleChange({
            handle: nextHandle,
            bounds: nextBounds
        })
    }

    /**
     * On mouse/touch end.
     */
    function onEnd(): void {
        setHandle(null)
    }

    /**
     * Clamp current value to min-max interval on align to available values
     * using step and marks.
     */
    function clampAlignValue(value: number, nextProps: Partial<MultiSliderProps> = {}): number {
        const mergedProps = { min, max, allowCross, marks, step, ...nextProps }
        return alignValue(clampValueToSurroundingHandles(clampValue(value, mergedProps), mergedProps), mergedProps)
    }

    /**
     * Get index of closes handle.
     */
    function getClosestBound(value: number): number {
        let closestBound: number = 0

        for (let i = 1, n = bounds.length - 1; i < n; i++) {
            if (value > bounds[i]!) {
                closestBound = i
            }
        }

        if (Math.abs(bounds[closestBound + 1]! - value) < Math.abs(bounds[closestBound]! - value)) {
            closestBound += 1
        }

        return closestBound
    }

    /**
     * Get index of handle that should be moved.
     */
    function getBoundNeedMoving(value: number, closestBound: number): number {
        let boundNeedMoving: number = closestBound

        const nextBound = closestBound + 1
        const atTheSamePoint = bounds[nextBound] === bounds[closestBound]

        if (atTheSamePoint) {
            if (bounds[recent] === bounds[closestBound]) {
                boundNeedMoving = recent
            } else {
                boundNeedMoving = nextBound
            }

            if (value !== bounds[nextBound]) {
                boundNeedMoving = value < bounds[nextBound]! ? closestBound : nextBound
            }
        }

        return boundNeedMoving
    }

    /**
     * Push surrounding handles.
     */
    function pushSurroundingHandles(bounds: number[], handle: number, originalValue: number): void {
        const threshold = Number(pushable)
        const value = bounds[handle]

        let direction = 0

        if (bounds[handle + 1]! - value! < threshold) {
            // Push to right
            direction = +1
        }

        if (value! - bounds[handle - 1]! < threshold) {
            // Push to left
            direction = -1
        }

        if (direction === 0) {
            return
        }

        const nextHandle = handle + direction
        const diffToNext = direction * (bounds[nextHandle]! - value!)

        if (!pushHandle(bounds, nextHandle, direction, threshold - diffToNext)) {
            // Revert to original value if pushing is impossible
            bounds[handle] = originalValue
        }
    }

    /**
     * Push specific handle.
     */
    function pushHandle(bounds: number[], handle: number, direction: number, amount: number): boolean {
        const originalValue = bounds[handle]
        let currentValue = bounds[handle]

        while (direction * (currentValue! - originalValue!) < amount) {
            if (!pushHandleOnePoint(bounds, handle, direction)) {
                // Can't push handle enough to create the needed `amount` gap,
                // so we revert its position to the original value.
                bounds[handle] = originalValue!

                return false
            }

            currentValue = bounds[handle]
        }

        // The handle was pushed enough to create the needed `amount` gap

        return true
    }

    /**
     * Push specific handle by one point.
     */
    function pushHandleOnePoint(bounds: number[], handle: number, direction: number): boolean {
        const points = getPoints()
        const pointIndex = points.indexOf(bounds[handle]!)
        const nextPointIndex = pointIndex + direction

        if (nextPointIndex >= points.length || nextPointIndex < 0) {
            // Reached the minimum or maximum available point, can't push anymore.
            return false
        }

        const nextHandle = handle + direction
        const nextValue = points[nextPointIndex]

        const threshold = Number(pushable)

        const diffToNext = direction * (bounds[nextHandle]! - nextValue!)

        if (!pushHandle(bounds, nextHandle, direction, threshold - diffToNext)) {
            // Couldn't push next handle, so we won't push this one either
            return false
        }

        // Push the handle
        bounds[handle] = nextValue!

        return true
    }

    /**
     * Returns an array of possible slider points, taking into account both
     * `marks` and `step`. The result is cached.
     */
    function getPoints(): number[] {
        const cache = pointsCache

        if (!cache || cache.marks !== marks || cache.step !== step) {
            const pointsObject = { ...marks }

            if (step != null && step > 0) {
                for (let point = min; point <= max; point += step) {
                    pointsObject[point] = String(point)
                }
            }

            const points = Object.keys(pointsObject).map(Number)

            points.sort((a, b) => a - b)
            setPointsCache({ marks, step, points })
        }

        return pointsCache?.points || []
    }

    /**
     * Clamp value to interval of surrounding handles.
     */
    function clampValueToSurroundingHandles(value: number, { allowCross }: { allowCross: boolean }): number {
        if (!allowCross && handle != null) {
            if (handle > 0 && value <= bounds[handle - 1]!) {
                return bounds[handle - 1]!
            }
            if (handle < bounds.length - 1 && value >= bounds[handle + 1]!) {
                return bounds[handle + 1]!
            }
        }

        return value
    }

    return (
        <AbstractSlider
            min={min}
            max={max}
            vertical={vertical}
            disabled={disabled}
            classesPrefix={classesPrefix}
            tracks={tracks}
            handles={handles}
            bounds={bounds}
            upperBound={bounds[bounds.length - 1]!}
            lowerBound={bounds[0]!}
            handlesRefs={handlesRefs.current}
            activeHandle={handle}
            step={step}
            marks={marks}
            dots={dots}
            included={included}
            className={className}
            onBeforeChange={onBeforeChange}
            onAfterChange={onAfterChange}
            onChange={onChange}
            onStart={onStart}
            onEnd={onEnd}
            onMove={onMove}
            sliderRefCallback={setSliderRef}
            tipFormatter={tipFormatter}
        >
            {children}
        </AbstractSlider>
    )
}
